import * as UI from "@iyk/ui"
import { Array, Effect, Option, Order } from "effect"
import { Assets } from "../../../packages/assets.ts"
import { Handler } from "../../../server/handler.ts"
import { Res } from "../../../server/response.ts"
import type { Profile } from "../profile.ts"
import { AlertCard } from "../ui/alert-card.tsx"
import { AudioPlayerProvider } from "../ui/audio-player/audio-player-provider.tsx"
import { ExclusiveCardList } from "../ui/exclusive-card-list.tsx"
import { ExclusiveEmptyCard } from "../ui/exclusive-empty-card.tsx"
import { Link, Routes } from "../ui/link.tsx"
import { ShowScheduleLink } from "../ui/show-schedule-item.tsx"
import { UserProfileCard } from "../ui/user-profile-card.tsx"
import type { Route } from "./+types/home.ts"

type LoaderData = {
  nextShow?: Profile.Show
  merchSkus: Array<Profile.Merch>
  exclusives: Array<Profile.Exclusive>
  totalShows: number
  checkInPrompt?: Profile.CheckInPromptInfo
}

export const loader = Handler.make<LoaderData>("profile-home", ({ api, sessions }) => {
  return Effect.all(
    {
      checkInPrompt: api.checkInPrompt(sessions).pipe(Effect.map(Option.getOrUndefined)),
      nextShow: api.findNextShow,
      merchSkus: api.listMerch,
      totalShows: api.listShows.pipe(Effect.map(Array.length)),
      exclusives: sessions.authenticated.pipe(
        Effect.flatMap((session) => api.findUnlockedExclusives(session.user.id)),
        Effect.orElseSucceed(() => []),
      ),
    },
    { concurrency: "unbounded" },
  ).pipe(Effect.map(Res.json))
})

export default function ProfileRoute(props: Route.ComponentProps) {
  const layoutData = props.matches[1].data
  const sortedSkus = Array.sortWith(
    props.loaderData.merchSkus,
    (sku) => (layoutData.user?.ownedMerchSkuIds.includes(sku.id) ? 0 : 1),
    Order.number,
  )

  return layoutData.user ? (
    <AudioPlayerProvider>
      <LoggedInProfile
        user={layoutData.user}
        exclusives={props.loaderData.exclusives}
        nextShow={props.loaderData.nextShow}
        totalShows={props.loaderData.totalShows}
        merchSkus={sortedSkus}
        checkInPrompt={props.loaderData.checkInPrompt}
      />
    </AudioPlayerProvider>
  ) : (
    <LoggedOutProfile
      nextShow={props.loaderData.nextShow}
      totalShows={props.loaderData.totalShows}
      merchSkus={props.loaderData.merchSkus}
      checkInPrompt={props.loaderData.checkInPrompt}
    />
  )
}

const LoggedInProfile = ({
  user,
  nextShow,
  totalShows,
  alert,
  merchSkus,
  exclusives,
  checkInPrompt,
}: LoggedInProfile.Props) => (
  <div className="flex flex-col gap-8 px-4">
    <UserProfileCard
      userId={user.id}
      username={user.username}
      since={user.memberSince}
      totalCheckIns={user.attendedShowIds.length}
      totalMerchOwned={user.ownedMerchSkuIds.length}
      variant="profile"
    />
    {alert && (
      <AlertCard
        eyebrow={alert.eyebrow}
        title={alert.title}
        subtitle={alert.subtitle}
        announcementTitle={alert.announcementTitle}
        announcementDate={alert.announcementDate}
        description={alert.description}
        kind={alert.kind}
      />
    )}
    {/* {checkInPrompt && (
      <CheckInCard
        title={`${checkInPrompt.show.venue}에 계신가요?`}
        description="체크인하면 추첨에 자동 응모되고, 독점 보상도 받을 수 있어요"
      />
    )} */}
    {/* {totalShows > 0 && <UploadFanClipsCard />} */}
    <section className="flex flex-col gap-4">
      <h2 className="text-base">보상들</h2>
      <div className="grid gap-2">
        {Array.isEmptyArray(exclusives) ? (
          <ExclusiveEmptyCard />
        ) : (
          <ExclusiveCardList owned={true} exclusives={exclusives} />
        )}
      </div>
    </section>
    {nextShow && <NextShow nextShow={nextShow} showCount={totalShows} />}
  </div>
)

namespace LoggedInProfile {
  export interface Props {
    user: Profile.User
    merchSkus: Array<Profile.Merch>
    exclusives: Array<Profile.Exclusive>
    totalShows: number
    nextShow?: Profile.Show
    alert?: AlertCard.Props
    checkInPrompt?: Profile.CheckInPromptInfo
  }
}

// TODO: add login button and auth sheet
const LoggedOutProfile = ({
  nextShow,
  totalShows,
  merchSkus,
  checkInPrompt,
}: LoggedOutProfile.Props) => (
  <div className="flex flex-col gap-8 px-4">
    <section
      style={{
        backgroundImage: `linear-gradient(rgb(0_0_0/30%),rgb(0_0_0/30%)),url('${Assets.images.emptyCollectionBackground}')`,
      }}
      className="flex flex-col items-center justify-center p-8 text-sm text-center text-balance relative bg-cover rounded [box-shadow:0_25px_50px_-12px_rgb(0_0_0/50%),rgb(100%_100%_100%/10%)_0_0_0_.5px_inset]"
    >
      <h1 className="max-w-[216px]">인증된 팬이 되어보세요</h1>
      <p className="mt-3 max-w-[216px] text-gray-11">아이템 등록하거나 쇼 체크인하여 시작하세요</p>
    </section>
    {/* {checkInPrompt && (
      <CheckInCard
        title={`${checkInPrompt.show.venue}에 계신가요?`}
        description="체크인하면 추첨에 자동 응모되고, 독점 보상도 받을 수 있어요"
      />
    )} */}
    {nextShow && (
      <section className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-base">다음 쇼</h2>
          <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
            전체 보기 ({totalShows})
          </Link>
        </div>
        {nextShow && (
          <ShowScheduleLink
            id={nextShow.id}
            city={nextShow.city}
            state={nextShow.state}
            dayOfMonth={nextShow.dayOfMonth}
            dayOfWeek={nextShow.dayOfWeek}
            time={nextShow.time}
            venue={nextShow.venue}
            url={nextShow.url}
            month={nextShow.month}
            checked={false}
          />
        )}
      </section>
    )}
  </div>
)

namespace LoggedOutProfile {
  export interface Props {
    nextShow?: Profile.Show
    totalShows: number
    merchSkus: Array<Profile.Merch>
    checkInPrompt?: Profile.CheckInPromptInfo
  }
}

const NextShow = ({ nextShow, showCount }: NextShow.Props) => (
  <section className="flex flex-col gap-4">
    <div className="flex justify-between items-center">
      <h2 className="text-base">다음 쇼</h2>
      <Link to={Routes.tourSchedule} className={UI.classForLink({ className: "text-sm" })}>
        모두 보기 ({showCount})
      </Link>
    </div>
    {nextShow && (
      <ShowScheduleLink
        id={nextShow.id}
        city={nextShow.city}
        state={nextShow.state}
        dayOfMonth={nextShow.dayOfMonth}
        dayOfWeek={nextShow.dayOfWeek}
        time={nextShow.time}
        venue={nextShow.venue}
        url={nextShow.url}
        month={nextShow.month}
        checked={false}
      />
    )}
  </section>
)

namespace NextShow {
  export interface Props {
    nextShow?: Profile.Show
    showCount: number
  }
}

const CheckInCard = ({ title, description }: CheckInCard.Props) => (
  <section className="flex flex-col gap-4 p-4 bshadow bshadow-gray-5">
    <div className="grid gap-1">
      <h3 className="text-sm text-gray-12">{title}</h3>
      <p className="text-sm text-gray-11">{description}</p>
    </div>
    <Link
      to={Routes.checkIn}
      className={UI.classForButton({ size: "sm", kind: "solid", className: "w-max" })}
    >
      체크인
    </Link>
  </section>
)

// @ts-ignore reason: this is not used yet, otherwise we get a typecheck error
const UploadFanClipsCard = () => (
  <section className="flex flex-col gap-4 p-4 bshadow bshadow-gray-5">
    <div className="grid gap-1">
      <h3 className="text-sm text-gray-12">콘텐츠를 업로드하기</h3>
      <p className="text-sm text-gray-11">
        Isegye Idol 공식 인스타그램에 소개될 기회를 위해 경험을 공유하세요
      </p>
    </div>
    <Link
      to={Routes.fanClipsNew}
      className={UI.classForButton({ size: "sm", kind: "solid", className: "w-max" })}
    >
      업로드
    </Link>
  </section>
)

namespace CheckInCard {
  export interface Props {
    title: string
    description: string
  }
}
